import { useTranslation } from '@silospot/i18n/react';
import { toast } from '@silospot/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ExternalLink, Loader2, RefreshCw } from 'lucide-react';
import { type ReactElement, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, serverError } from '../../lib/api';
import { relTime } from '../../lib/watched-audit';
import { Badge, Btn, Card, CardHead } from './kit';

/**
 * Settings → Integrations → Google Ads (read-only, workspace level). Enriched
 * summary for THIS site: connected account, monitored-campaign count, last sync,
 * with actions (Manage / Change account / Sync now / Reconnect / Disconnect with
 * confirmation). The full account→campaigns flow lives on the Manage page.
 */
export function GoogleAdsCard({ projectId }: { projectId: string }): ReactElement {
  const { t, i18n } = useTranslation('pages');
  const lang = i18n.language || 'fr';
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [discAsk, setDiscAsk] = useState(false);

  const statusQ = useQuery({ queryKey: ['gads-status'], queryFn: () => api.googleAdsStatus() });
  const status = statusQ.data;
  const connected = !!status?.connected;

  const linkedQ = useQuery({
    queryKey: ['gads-site-accounts', projectId],
    queryFn: () => api.googleAdsSiteAccounts(projectId),
    enabled: connected,
  });
  const account = linkedQ.data?.accounts[0] ?? null;

  const watchedQ = useQuery({
    queryKey: ['gads-watched', projectId, account?.customerId],
    queryFn: () => api.googleAdsWatched(projectId, account?.customerId as string),
    enabled: connected && !!account,
  });
  const watched = watchedQ.data;

  // Handle the OAuth return (?google_ads=connected|error|expired).
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const result = params.get('google_ads');
    if (!result) return;
    if (result === 'connected') {
      toast.success(t('gads.toastConnected'));
      void qc.invalidateQueries({ queryKey: ['gads-status'] });
    } else if (result === 'expired') {
      toast.info(t('gads.toastExpired'));
    } else {
      toast.error(t('gads.toastError'));
    }
    params.delete('google_ads');
    const qs = params.toString();
    window.history.replaceState({}, '', `${window.location.pathname}${qs ? `?${qs}` : ''}`);
  }, [qc, t]);

  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ['gads-status'] });
    void qc.invalidateQueries({ queryKey: ['gads-site-accounts', projectId] });
    void qc.invalidateQueries({ queryKey: ['gads-watched'] });
  };

  const connect = useMutation({
    mutationFn: () => api.googleAdsConnect(`/projects/${projectId}/settings`),
    onSuccess: ({ url }) => {
      window.location.href = url;
    },
    onError: (e) => toast.error(serverError(e) ?? t('gads.toastError')),
  });
  const syncNow = useMutation({
    mutationFn: () => api.googleAdsSyncNow(projectId, account?.customerId as string),
    onSuccess: () => {
      toast.success(t('gadsm.syncDone'));
      refresh();
    },
    onError: (e) => toast.error(serverError(e) ?? t('gads.errGeneric')),
  });
  const disconnect = useMutation({
    mutationFn: () => api.googleAdsDisconnect(),
    onSuccess: () => {
      setDiscAsk(false);
      toast.info(t('gadsm.disconnected'));
      refresh();
    },
  });
  // Per-site unlink (removes only THIS site's link) — distinct from the org-level
  // disconnect above, which cuts Google Ads for every site.
  const unlink = useMutation({
    mutationFn: () => api.googleAdsUnlinkAccount(projectId, account?.customerId as string),
    onSuccess: () => {
      toast.info(t('gadsm.unlinked'));
      refresh();
    },
    onError: (e) => toast.error(serverError(e) ?? t('gads.errGeneric')),
  });

  return (
    <Card className="lg:col-span-2">
      <CardHead title={t('gads.title')} sub={t('gads.desc')} />

      {!status ? (
        <div className="h-10 animate-pulse rounded bg-neutral-bg" />
      ) : !status.configured ? (
        <div className="flex items-center gap-2 text-[13px] text-text-3">
          <span className="grid h-5 w-5 place-items-center rounded-full bg-neutral-bg text-[11px]">
            —
          </span>
          {t('gads.notConfigured')}
        </div>
      ) : !connected ? (
        <div className="flex flex-wrap items-center gap-3">
          <Btn primary disabled={connect.isPending} onClick={() => connect.mutate()}>
            {connect.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
            {connect.isPending ? t('gads.connecting') : t('gads.connect')}
          </Btn>
          <span className="text-[12px] text-text-3">{t('gads.readonly')}</span>
        </div>
      ) : (
        <div>
          <div className="mb-3 flex flex-wrap items-center gap-2">
            <Badge tone={account ? 'clean' : 'suspect'} dot>
              {account ? t('gads.connected') : t('gads.connectedNoAccount')}
            </Badge>
            <span className="text-[12px] text-text-3">{t('gads.readonly')}</span>
          </div>

          {!account ? (
            // Connected but no account linked to this site yet → go to the flow.
            <div className="rounded-xl border border-border bg-bg/40 p-3.5">
              <div className="text-[13px] font-semibold text-text">{t('gadsm.connectTitle')}</div>
              <p className="mt-0.5 text-[12.5px] text-text-2">{t('gadsm.connectSub')}</p>
              <div className="mt-3 flex flex-wrap gap-2">
                <Btn primary onClick={() => navigate(`/projects/${projectId}/ads/manage`)}>
                  {t('gadsm.linkAccount')}
                </Btn>
              </div>
            </div>
          ) : (
            <div className="rounded-xl border border-border bg-bg/40 p-3.5">
              {/* Summary: account + monitored count + last sync */}
              <div className="flex flex-wrap items-end justify-between gap-3">
                <div>
                  <div className="text-[14px] font-semibold text-text">
                    {account.name || t('gads.account')}
                  </div>
                  <div className="font-mono text-[11.5px] text-text-3">{account.customerId}</div>
                </div>
                <div className="flex gap-6 text-right">
                  <div>
                    <div className="font-mono text-[18px] font-bold text-text">
                      {watchedQ.isLoading ? '—' : (watched?.watched.length ?? 0)}
                    </div>
                    <div className="text-[11px] text-text-3">{t('gadsm.kpiWatched')}</div>
                  </div>
                  <div>
                    <div className="text-[13px] font-semibold text-text">
                      {relTime(watched?.lastSyncAt ?? null, lang)}
                    </div>
                    <div className="text-[11px] text-text-3">{t('gadsm.kpiLastSync')}</div>
                  </div>
                </div>
              </div>

              <div className="mt-3.5 flex flex-wrap gap-2">
                <Btn primary onClick={() => navigate(`/projects/${projectId}/ads/manage`)}>
                  {t('gadsm.manage')}
                </Btn>
                <Btn onClick={() => navigate(`/projects/${projectId}/ads/manage`)}>
                  {t('gadsm.changeAccount')}
                </Btn>
                <Btn icon={RefreshCw} disabled={syncNow.isPending} onClick={() => syncNow.mutate()}>
                  {t('gadsm.syncNow')}
                </Btn>
                <Btn disabled={connect.isPending} onClick={() => connect.mutate()}>
                  {t('gadsm.reconnect')}
                </Btn>
                <Btn disabled={unlink.isPending} onClick={() => unlink.mutate()}>
                  {t('gadsm.unlinkAccount')}
                </Btn>
                <Btn danger onClick={() => setDiscAsk(true)}>
                  {t('gadsm.disconnectAll')}
                </Btn>
              </div>
            </div>
          )}
        </div>
      )}

      <a
        href="https://ads.google.com"
        target="_blank"
        rel="noreferrer"
        className="mt-3 inline-flex items-center gap-1 text-[11.5px] text-text-3 hover:text-text-2"
      >
        ads.google.com <ExternalLink className="h-3 w-3" />
      </a>

      {discAsk ? (
        <div
          className="fixed inset-0 z-[120] grid place-items-center bg-[rgba(15,21,32,0.55)] p-4"
          onClick={() => setDiscAsk(false)}
          onKeyDown={(e) => e.key === 'Escape' && setDiscAsk(false)}
          role="presentation"
        >
          <div
            className="w-full max-w-[420px] rounded-2xl bg-surface p-6 shadow-[0_24px_64px_rgba(16,24,40,0.28)]"
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => e.stopPropagation()}
            role="presentation"
          >
            <h2 className="text-[18px] font-bold text-text">{t('gadsm.discTitle')}</h2>
            <p className="mt-2 text-[13.5px] leading-relaxed text-text-2">{t('gadsm.discBody')}</p>
            <div className="mt-5 flex justify-end gap-2">
              <Btn onClick={() => setDiscAsk(false)}>{t('gadsm.cancel')}</Btn>
              <Btn danger disabled={disconnect.isPending} onClick={() => disconnect.mutate()}>
                {t('gadsm.disconnectAll')}
              </Btn>
            </div>
          </div>
        </div>
      ) : null}
    </Card>
  );
}
